import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Input from "../../common/components/Input/Input";
import { useForm } from "react-hook-form";
import {
  getNewProjectSelector,
  INewProjectFormValues,
  setNewProject,
  getEditedProject,
  setEditedName,
  getCurrentProject,
  updateProject,
  projectsSelector,
} from "../../features/projects/state/projectsSlice";
import {
  fetchSpecificProjectGroupWithProjects,
  getCurrentElements,
  getSelectedProjectGroup,
  setCurrentElements,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { useAppDispatch } from "../../common/state/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../common/state/store";
import { useEffect, useState } from "react";
import { FooterButtons } from "../../common/components/FooterButtons";
import isStringLegalProjectName from "../../common/util/isStringLegalProjectName";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { loadGraph } from "src/features/graph/state/graphLegacy";
import { PROJECT_LIST_COMPONENT_ID } from "src/features/projects/components/ProjectList";
import { setAlert } from "src/features/alert/state/alertsSlice";

interface IProjectNewName {
  incrementStep?: () => void;
  decrementStep?: () => void;
  mode?: string;
  onCancelClick?: () => void;
}

const ProjectNewName = ({ incrementStep, mode = "", onCancelClick = null }: IProjectNewName) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<INewProjectFormValues>();

  const selectedProjectElements = useSelector(getCurrentElements);

  const {
    allProjects,
    newProject,
    selectedProjectGroup,
    editedProject,
    currentProject,
    currentUser,
  } = useSelector((state: RootState) => {
    return {
      allProjects: projectsSelector(state),
      newProject: getNewProjectSelector(state),
      selectedProjectGroup: getSelectedProjectGroup(state),
      editedProject: getEditedProject(state),
      currentProject: getCurrentProject(state),
      currentUser: getCurrentUser(state),
    };
  });

  const [inputValueNewProject, setinputValueNewProject] = useState<string>(
    currentProject?.name ?? "",
  );
  const userPreferredLanguage = currentUser.preferred_language?.id;

  const onSubmit = (data: INewProjectFormValues) => {
    if (isStringLegalProjectName(inputValueNewProject)) {
      switch (mode) {
        case "edit-name":
          dispatch(setEditedName(data.newName));
          break;
        case "faitTemplate":
          dispatch(
            setNewProject({
              newName: data.newName,
              projectId: Number(currentProject.id),
              thumbnailId: 0,
              projectGroupId: 0,
              languageId: userPreferredLanguage ?? 1,
              enabledLanguagesIds: userPreferredLanguage ? [String(userPreferredLanguage)] : [],
            }),
          );
          break;
        case "duplicateProject":
          dispatch(
            setNewProject({
              newName: data.newName,
              projectId: Number(currentProject.id),
              thumbnailId: 0,
              projectGroupId: selectedProjectGroup.id,
              languageId: currentProject.enabled_languages?.[0]?.id ?? 1,
              enabledLanguagesIds: currentProject.enabled_languages?.map((l) => String(l.id)) ?? [],
            }),
          );
          break;
        default:
          dispatch(
            setNewProject({
              ...newProject,
              newName: data.newName,
              projectGroupId: selectedProjectGroup.id,
            }),
          );
          break;
      }

      if (incrementStep) incrementStep();
    }
  };

  const handleSave = () => {
    if (mode === "edit-name") {
      dispatch(
        updateProject({
          id: currentProject.id,
          payload: {
            name: inputValueNewProject,
            enabled_languages: editedProject.enabledLanguagesIds,
          },
          componentId: PROJECT_LIST_COMPONENT_ID,
        }),
      ).then((res) => {
        const updatedProjectElements = [...selectedProjectElements].map((element) => {
          if (element.project.id === res.payload.id) {
            return {
              ...element,
              project: res.payload,
            };
          }

          return element;
        });
        dispatch(setAlert({ msg: t("alerts.projectUpdateSuccess"), type: "success" }));
        dispatch(
          fetchSpecificProjectGroupWithProjects({ projectGroupId: selectedProjectGroup.id }),
        );
        dispatch(setCurrentElements(updatedProjectElements));
        dispatch(loadGraph(currentProject.id));
      });
      navigate(cancelUrl);
    }
  };

  useEffect(() => {
    if (mode === "edit-name") {
      setValue("newName", currentProject?.name ?? "");
      setinputValueNewProject(currentProject?.name ?? "");
      trigger("newName");
    } else if (mode !== "faitTemplate" && mode !== "duplicateProject") {
      setValue("newName", newProject.newName);
      setinputValueNewProject(newProject.newName);
    }
  }, [currentProject?.name, newProject.newName, mode, setValue, trigger]);

  const title = () => {
    switch (mode) {
      case "edit-name":
        return t("pages.editProjectName.pleaseRename");
      case "faitTemplate":
        return t("pages.editProjectName.editTemplateName");
      default:
        return t("chooseTemplateType.projectName");
    }
  };

  const cancelUrl =
    currentProject?.id && path[path.length - 2] === "graph"
      ? `/courses/${selectedProjectGroup.id}/list/${currentProject.id}/graph`
      : selectedProjectGroup.id
      ? `/courses/${selectedProjectGroup.id}/list`
      : "/courses";

  const handleCancel = () => {
    navigate(cancelUrl);
  };

  const projectNamesInsideThisProjectGroup = selectedProjectGroup.project_group_elements
    ? [...selectedProjectGroup.project_group_elements]
        .sort((a, b) => a.order - b.order)
        .map((pge) => pge.project?.name)
        .filter((name): name is string => Boolean(name))
    : [];

  const customTemplateNames = allProjects
    .filter((project) => {
      const tags = project?.project_tags;
      // Defensive check: must be an array, must have at least one tag
      if (Array.isArray(tags) && tags.length > 0) {
        return tags[0]?.slug === "custom-templates";
      }
      return false;
    })
    .map((project) => project.name.toLowerCase());

  useEffect(() => {
    if (errors.newName?.type === "projectWithThisNameAlreadyExists") {
      setinputValueNewProject("");
    }
  }, [errors.newName?.type]);

  return (
    <>
      <h1 className="text-2xl my-5 mx-6 font-medium">{title()}</h1>
      <form className="flex flex-col grow w-full justify-between" onSubmit={handleSubmit(onSubmit)}>
        <div className="mx-6 max-w-md">
          <Input
            autoFocus
            type="text"
            placeholder={t("chooseTemplateType.placeholder")}
            registerFct={() =>
              register("newName", {
                required: true,
                maxLength: 24,
                value: currentProject?.name ?? "",
                onChange(event) {
                  setinputValueNewProject(event.target.value);
                  trigger("newName");
                },
                validate: {
                  notEmptyOrSpaces: (value) =>
                    value.trim() !== "" || t("pages.project.notPossibleToNameWithEmptyCharProject"),
                  projectWithThisNameAlreadyExists: (v) => {
                    if (mode === "faitTemplate") {
                      return true;
                    }
                    return (
                      !projectNamesInsideThisProjectGroup.includes(v) ||
                      (v === currentProject?.name && mode === "edit-name")
                    );
                  },
                  templateWithThisNameAlreadyExists: (value) => {
                    if (mode !== "faitTemplate") {
                      return true;
                    }
                    return !customTemplateNames.includes(value.toLowerCase());
                  },
                },
              })
            }
          />
          {errors.newName?.type === "maxLength" && (
            <span className="text-red-600 text-sm mt-2">{t("alerts.maxCharacters")}</span>
          )}
          {!isStringLegalProjectName(inputValueNewProject) && (
            <span className="text-red-600 text-sm mt-2">{t("alerts.wrongProjectNameFormat")}</span>
          )}
          {errors.newName?.type === "notEmptyOrSpaces" && (
            <span className="text-red-600 text-sm mt-2">
              {t("pages.project.notPossibleToNameWithEmptyCharProject")}
            </span>
          )}
          {errors.newName?.type === "required" && (
            <span className="text-red-600 text-sm mt-2">{t("stepper.nameProjectValidation")}</span>
          )}
          {errors.newName?.type === "projectWithThisNameAlreadyExists" && (
            <span className="text-red-600 text-sm mt-2">
              {t("alerts.aProjectWithThisNameAlreadyExistsInTheProjectGroup")}
            </span>
          )}
          {errors.newName?.type === "templateWithThisNameAlreadyExists" && (
            <span className="text-red-600 text-sm mt-2">
              {t("alerts.templateNameAlreadyExists")}
            </span>
          )}
        </div>
        {mode === "edit-name" ? (
          <FooterButtons
            cancelCb={handleCancel}
            cancelText={t("general.cancel")}
            nextText={t("general.save")}
            nextCb={handleSave}
            nextDisabled={
              errors.newName?.type === "projectWithThisNameAlreadyExists" ||
              errors.newName?.type === "templateWithThisNameAlreadyExists" ||
              errors.newName?.type === "notEmptyOrSpaces" ||
              errors.newName?.type === "required" ||
              errors.newName?.type === "maxLength" ||
              isStringLegalProjectName(inputValueNewProject) === false
            }
          />
        ) : (
          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() => {
              if (onCancelClick) {
                onCancelClick();
              } else {
                if (path[1] === "templates") {
                  navigate("/templates");
                } else {
                  navigate(cancelUrl);
                }
              }
            }}
            backText={t("general.back")}
            backDisabled={mode === "edit-name" || mode === "faitTemplate"}
            backCb={() => navigate(-1)}
            nextText={t("general.next")}
            nextDisabled={
              errors.newName?.type === "projectWithThisNameAlreadyExists" ||
              errors.newName?.type === "templateWithThisNameAlreadyExists" ||
              errors.newName?.type === "notEmptyOrSpaces" ||
              errors.newName?.type === "maxLength" ||
              isStringLegalProjectName(inputValueNewProject) === false
            }
          />
        )}
      </form>
    </>
  );
};

export default ProjectNewName;
